/**
 * GenerationSummaryBanner
 *
 * Collapsible banner showing how the current meal plan was built.
 * Displays summary lines with icons for taste profile, inventory, deals, and variety.
 */

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import {
  Paprika,
  PaprikaSpacing,
  PaprikaRadius,
  PaprikaTypography,
} from '../../styles/theme';
import { GenerationSummary, SummaryLine } from '../../services/generationSummaryService';

interface GenerationSummaryBannerProps {
  summary: GenerationSummary;
}

export const GenerationSummaryBanner: React.FC<GenerationSummaryBannerProps> = ({ summary }) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || summary.lines.length === 0) return null;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Feather name="info" size={14} color={Paprika.accent} style={styles.headerIcon} />
        <Text style={styles.headerText}>{t('generationSummary.howPlanBuilt')}</Text>
        <TouchableOpacity
          onPress={() => setDismissed(true)}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityLabel={t('common.dismiss')}
        >
          <Feather name="x" size={16} color={Paprika.textSecondary} />
        </TouchableOpacity>
      </View>
      <View style={styles.lines}>
        {summary.lines.map((line, index) => (
          <View key={`${line.type}-${index}`} style={styles.lineRow}>
            <Feather
              name={line.icon as any}
              size={14}
              color={Paprika.accent}
              style={styles.lineIcon}
            />
            <Text style={styles.lineText}>{line.text}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.sm,
    backgroundColor: Paprika.accentLight,
    borderRadius: PaprikaRadius.md,
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 2px rgba(82,68,60,0.06)',
      },
      ios: {
        shadowColor: '#52443c',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.06,
        shadowRadius: 2,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: PaprikaSpacing.xs,
  },
  headerIcon: {
    marginRight: PaprikaSpacing.xs,
  },
  headerText: {
    ...PaprikaTypography.captionBold,
    color: Paprika.textPrimary,
    flex: 1,
  },
  lines: {
    gap: 4,
  },
  lineRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  lineIcon: {
    marginRight: PaprikaSpacing.xs,
    width: 18,
  },
  lineText: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
    flex: 1,
  },
});

export default GenerationSummaryBanner;
